import { useAtomValue, useSetAtom } from "jotai"
import { useTranslation } from "react-i18next"
import {
	DEFAULT_IMPORTABLE_PRESET_EXTENSIONS,
	DEFAULT_PRESET_GROUP,
	LIGHTROOM_IMPORTABLE_PRESET_EXTENSIONS,
	currentPresetAtom,
	exportPreset,
	importPreset,
	loadingPresetsAtom,
	parsePresetIdAndStrength,
	polarrPresetsAtom,
	removePreset,
	removePresetGroup,
	renamePreset,
	renamePresetGroup,
	updatePresetGroup,
	userPresetsAtom,
} from "@/store/store-presets"
import type { PresetImportExtension } from "@/store/store-presets"
import clsx from "clsx"
import { ControlGroup } from "./ControlGroup"
import { ContextMenuItem, ContextMenuSeparator } from "./ui/ContextMenu"
import { queueApplyPreset } from "@/store/processing/image-processing"
import { isCalibrationAtom, isManualModeAtom, projectAtom } from "@/store/project/projects-state"
import {
	anchorPhotosAtom,
	currentPhotoAtom,
	isExpressSetupAtom,
	selectedPhotosAtom,
} from "@/store/photo/photos-state"
import { batchApplyPreset } from "@/store/style/apply-style"
import React, { MouseEventHandler, Suspense, useCallback, useEffect, useState } from "react"
import { Icon } from "@/components/icons/Icon"
import { editingDisabledAtom, hasShownOnboarding, markOnboardingShown } from "@/store/app-state"
import { pushDialog, replaceDialog, resetDialogs } from "@/store/dialogs-state"
import { ConfirmDialog, ImageStyle } from "./Dialogs/ConfirmDialog"
import { CreatePresetDialog } from "./Dialogs/SelectAdjustmentsDialog"
import { InputDialog } from "./Dialogs/InputDialog"
import { Button } from "./ui/Button"
import { showContextMenu } from "@/components/ContextMenuManager"
import { analytics } from "@/telemetry/amplitude"
import { Slider } from "./ui/Slider"
import { currentHoverPreviewAtom, previewPreset } from "@/store/editor/photo-preview"
import { PresetItem } from "@/store/presets/presets-service"
import { DataSource } from "@/store/data-source"
import { expandPanel, expandedPanelsAtom } from "@/store/ui-views-state"
import autoEnhancePngUrl from "@assets/auto_enhance.png?url"
import stylePngUrl from "@assets/style.png?url"
import { SettingsDialog } from "./Dialogs/SettingsDialog"
import { applyAIAdjustmentsAtom } from "@/store/settings-state"
import { Spinner } from "./ui/Spinner"
import { ErrorMap, isAbortError, isUnsupportedLRTemplateError } from "@/utils/error"
import { presetByGroupMapAtom } from "@/store/presets/presets-state"
import { Sentry } from "@/telemetry/sentry"
import { MovePresetDialog } from "./Dialogs/PresetDialogs"
import { getCompanyNameShort, getProductName } from "@/utils/product-name"
import { isLRPresetsEnabled, isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { packagedPresetCategoryKeys, getPackagedPresetDisplayName } from "@/i18n/dual-role-strings"

const PANEL_EXPAND_ANIMATION_MS = 220
const getPresetItemDomId = (presetId: string) => `preset-item-${encodeURIComponent(presetId)}`

type PresetsViewProps = {
	scrollToPresetId?: string | null
	onScrollToPresetDone?: () => void
}

export const PresetsView = ({ scrollToPresetId, onScrollToPresetDone }: PresetsViewProps) => {
	const { t } = useTranslation("components")
	const { data: userPresets } = useAtomValue(userPresetsAtom)
	const presetGroups = useAtomValue(presetByGroupMapAtom)
	const userPresetsGroup = presetGroups[DEFAULT_PRESET_GROUP] ?? []
	const customGroups = Object.entries(presetGroups)
		.filter(([group, _]) => group !== DEFAULT_PRESET_GROUP)
		.sort(([a, _aList], [b, _blist]) => a.localeCompare(b))

	const isCalibration = useAtomValue(isCalibrationAtom)
	const isExpressSetup = useAtomValue(isExpressSetupAtom)
	const isManualMode = useAtomValue(isManualModeAtom)
	const applyAIAdjustments = useAtomValue(applyAIAdjustmentsAtom)
	const suggestedPhotos = useAtomValue(anchorPhotosAtom)
	const polarrPresets = useAtomValue(polarrPresetsAtom)
	const currentProject = useAtomValue(projectAtom)
	const selectedPhotos = useAtomValue(selectedPhotosAtom)
	const currentPhoto = useAtomValue(currentPhotoAtom)
	const currentPresetInfo = useAtomValue(currentPresetAtom)
	const loadingPresets = useAtomValue(loadingPresetsAtom)
	const [presetStrength, setPresetStrength] = useState(1)
	const setCurrentHoverPreview = useSetAtom(currentHoverPreviewAtom)
	const [isApplyingToCurrent, setIsApplyingToCurrent] = useState(false)

	const isDisabled = selectedPhotos.length === 0 || !currentProject

	useEffect(() => {
		if (currentPresetInfo?.preset) {
			setPresetStrength(currentPresetInfo.strength)
		} else {
			setPresetStrength(1)
		}
	}, [currentPresetInfo?.preset?.id, currentPresetInfo?.strength])

	useEffect(() => {
		if (!scrollToPresetId) {
			return
		}

		const presetButton = document.getElementById(
			getPresetItemDomId(scrollToPresetId),
		) as HTMLButtonElement | null
		if (!presetButton) {
			return
		}

		presetButton.scrollIntoView({ behavior: "smooth", block: "nearest" })
		onScrollToPresetDone?.()
	}, [onScrollToPresetDone, presetGroups, scrollToPresetId, userPresets])

	const applyPreset = (preset: PresetItem, strength: number) => {
		let appliedToSuggested = false
		setIsApplyingToCurrent(true)
		const onCompletionOnCurrentPhoto = () => {
			setIsApplyingToCurrent(false)
		}
		if (selectedPhotos.length > 1) {
			batchApplyPreset(selectedPhotos, preset, strength, false, onCompletionOnCurrentPhoto)
			appliedToSuggested = selectedPhotos.some((photo) => photo.isAnchorImage)
		} else if (currentPhoto) {
			queueApplyPreset(currentPhoto, preset, strength, true, onCompletionOnCurrentPhoto)
			appliedToSuggested = currentPhoto.isAnchorImage
		}

		//Also apply to suggested photos if it's during setup
		let didApplyToAllSuggested = false
		if (appliedToSuggested && isCalibration && !isExpressSetup) {
			const selectedPhotoIds = new Set(selectedPhotos.map((photo) => photo.id))
			if (currentPhoto) {
				selectedPhotoIds.add(currentPhoto.id)
			}
			const validPhotos = suggestedPhotos.filter(
				(photo) => photo.kind == "default" && !selectedPhotoIds.has(photo.id),
			)
			if (validPhotos.length > 0) {
				batchApplyPreset(validPhotos, preset, strength, false)
				didApplyToAllSuggested = true
			}
		}

		showAutoEnhanceOnboarding()
			.then(() => {
				if (didApplyToAllSuggested) {
					showApplySuggestedOnboarding()
				}
			})
			.catch((error) => {
				console.error("Failed to show auto enhance onboarding:", error)
			})
	}

	const showAutoEnhanceOnboarding = () => {
		return new Promise<void>((resolve) => {
			if (isManualMode || !applyAIAdjustments || hasShownOnboarding("presetAppliedShown")) {
				resolve()
				return
			}

			replaceDialog(ConfirmDialog, {
				imageSource: autoEnhancePngUrl,
				imageStyle: "fit" as ImageStyle,
				title: t("presetsPanel.autoLightingTitle"),
				message: t("presetsPanel.autoLightingMessage"),
				primary: {
					title: t("common:action.continue"),
					handler: () => {
						markOnboardingShown("presetAppliedShown")
						resolve()
					},
				},
				trackingViewName: "onboarding_preset_auto_enhance",
			})
		})
	}

	const showApplySuggestedOnboarding = () => {
		if (hasShownOnboarding("presetAppliedToSuggestedShown")) {
			return
		}

		markOnboardingShown("presetAppliedToSuggestedShown")

		const isPixieset = isPixiesetAuthEnabled()

		pushDialog(ConfirmDialog, {
			imageSource: stylePngUrl,
			imageStyle: "fit" as ImageStyle,
			title: isPixieset
				? t("presetsPanel.presetAppliedPixieset")
				: t("presetsPanel.presetAppliedSuggested"),
			message: isPixieset
				? t("presetsPanel.presetAppliedSuggestedMessagePixieset")
				: t("presetsPanel.presetAppliedSuggestedMessage"),
			primary: {
				title: t("common:action.continue"),
			},
			...(!isPixieset && {
				secondary: {
					title: t("presetsPanel.openSettings"),
					handler: () => {
						replaceDialog(SettingsDialog)
					},
				},
			}),
			trackingViewName: "onboarding_preset_applied_suggested",
		})
	}

	const adjustPresetStrength = (strength: number) => {
		setPresetStrength(strength)
		if (currentPresetInfo?.preset) {
			applyPreset(currentPresetInfo?.preset, strength)
		}
	}

	const showRenamePresetDialog = (preset: PresetItem) => {
		replaceDialog(InputDialog, {
			title: t("presetsPanel.renamePreset"),
			buttonTitle: t("presetsPanel.rename"),
			initialInput: preset.name,
			trackingViewName: "rename_preset_dialog",
			confirmWithEnter: true,
			onConfirm: (name) => {
				resetDialogs()
				renamePreset(preset, name)
			},
		})
	}

	const showRenamePresetGroupDialog = (group: string) => {
		replaceDialog(InputDialog, {
			title: t("presetsPanel.renameGroup"),
			buttonTitle: t("presetsPanel.rename"),
			initialInput: group,
			trackingViewName: "rename_preset_group_dialog",
			confirmWithEnter: true,
			onConfirm: (newName: string) => {
				resetDialogs()
				const presets = presetGroups[group]
				if (newName !== group && presets && presets.length > 0) {
					renamePresetGroup(newName, presets)
				}
			},
		})
	}

	const showMovePresetDialog = (preset: PresetItem) => {
		replaceDialog(MovePresetDialog, {
			preset,
			onConfirm: (group) => {
				updatePresetGroup(preset, group)
			},
		})
	}

	const showMigratePresetDialog: MouseEventHandler<SVGSVGElement> = (evt) => {
		evt.stopPropagation()
		replaceDialog(ConfirmDialog, {
			title: t("presetsPanel.presetMigrateTitle", { productName: getProductName() }),
			message: t("presetsPanel.presetMigrateMessage"),
			primary: { title: t("common:action.confirm") },
			trackingViewName: "preset_migration_prompt",
		})
	}

	const openPresetMenu = (e: React.MouseEvent, preset: PresetItem) => {
		e.stopPropagation()

		if (preset.readOnly) {
			return
		}

		showContextMenu(
			e,
			<div aria-label={t("presetsPanel.ariaPresetMenu")}>
				<ContextMenuItem
					trackingLabel="rename"
					onSelect={() => showRenamePresetDialog(preset)}
				>
					{t("presetsPanel.rename")}
				</ContextMenuItem>
				<ContextMenuItem trackingLabel="move" onSelect={() => showMovePresetDialog(preset)}>
					{t("presetsPanel.moveToGroup")}
				</ContextMenuItem>
				<ContextMenuItem trackingLabel="export" onSelect={() => exportPreset(preset)}>
					{t("presetsPanel.export")}
				</ContextMenuItem>
				<ContextMenuItem trackingLabel="remove" onSelect={() => removePreset(preset)}>
					{t("presetsPanel.remove")}
				</ContextMenuItem>
			</div>,
		)
	}

	const openPresetGroupMenu = (e: React.MouseEvent, group: string) => {
		e.stopPropagation()

		showContextMenu(
			e,
			<div aria-label={t("presetsPanel.ariaPresetGroupMenu")}>
				<ContextMenuItem
					trackingLabel="rename"
					onSelect={() => showRenamePresetGroupDialog(group)}
				>
					{t("presetsPanel.rename")}
				</ContextMenuItem>
				<ContextMenuItem
					trackingLabel="remove"
					onSelect={() => removePresetGroup(presetGroups[group] ?? [], group)}
				>
					{t("presetsPanel.remove")}
				</ContextMenuItem>
			</div>,
		)
	}

	const canPresetBeApplied = (preset: PresetItem) => {
		return (
			selectedPhotos.length == 0 ||
			selectedPhotos.filter((photo) => {
				const presetId = photo.presetId
					? parsePresetIdAndStrength(photo.presetId).presetId
					: null
				return presetId !== preset.id
			}).length > 0
		)
	}

	const renderPresetsList = (presets: PresetItem[]) => {
		return (
			<div
				onMouseLeave={() => {
					if (isApplyingToCurrent) {
						return
					}
					setCurrentHoverPreview(null)
				}}
			>
				{presets.length == 0 && (
					<div className="p-2 text-left text-xs text-base-500">
						{t("presetsPanel.noPresetsImported")}
					</div>
				)}
				{presets
					.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
					.map((preset) => (
						<div
							key={preset.id}
							className="group my-0.5 flex w-full flex-row items-center justify-between space-x-2 self-start text-left text-xs"
						>
							<button
								id={getPresetItemDomId(preset.id)}
								tabIndex={0}
								aria-label={t("presetsPanel.ariaApplyPreset", {
									name: getPresetDisplayName(preset),
								})}
								aria-current={currentPreset?.id === preset.id ? "true" : undefined}
								aria-disabled={!canPresetBeApplied(preset)}
								className={clsx(
									canPresetBeApplied(preset)
										? "bg-transparent text-base-300 hover:bg-base-700 hover:text-base-300 data-[selected]:bg-base-700"
										: "bg-base-300 text-base-800",
									"menu-root flex min-w-0 flex-1 flex-row items-center justify-between self-start rounded-sm px-2 py-1",
								)}
								onClick={(e) => {
									analytics.trackEvent(
										{
											label: "apply_preset",
											type: "click",
											value: preset.id,
										},
										e.target,
									)
									setPresetStrength(1)
									applyPreset(preset, 1)
								}}
								onMouseEnter={() => {
									setCurrentHoverPreview({ ...preset, type: "preset" })
								}}
								onContextMenu={(e) => {
									analytics.trackEvent(
										{
											label: "open_preset_menu",
											type: "click",
											clickType: "right_click",
											value: preset.id,
										},
										e.target,
									)
									openPresetMenu(e, preset)
								}}
							>
								<div className="truncate">{getPresetDisplayName(preset)}</div>
							</button>

							<div className="flex flex-row space-x-2">
								{loadingPresets[preset.id] && (
									<Spinner className="mt-0.5" size={12} />
								)}

								{!preset.readOnly && (
									<Button
										trackingLabel="open_preset_menu"
										variant="custom"
										size="custom"
										className="invisible -m-1 p-0.5 hover:text-white group-hover:visible"
										onClick={(e) => openPresetMenu(e, preset)}
									>
										<Icon name="more" />
									</Button>
								)}
								{preset.source !== DataSource.Backend &&
									userPresets.includes(preset) && (
										<Icon
											size={16}
											className="text-base-300 hover:text-white"
											name="error"
											onClick={showMigratePresetDialog}
										/>
									)}
							</div>
						</div>
					))}
			</div>
		)
	}

	const currentPreset = currentPresetInfo?.preset
	const renderPresetStrength = () => {
		return (
			<div className="sticky top-0 z-10 flex flex-none flex-col border-b border-base-900 bg-base-800 px-3 py-2">
				<div
					aria-disabled={currentPreset == null}
					className="text-sm text-base-100 aria-disabled:opacity-50"
				>
					{currentPreset
						? t("presetsPanel.presetLabel", {
								name: getPresetDisplayName(currentPreset),
							})
						: t("presetsPanel.presetLabelNone")}
				</div>
				<Slider
					className="ml-[-12px] aria-disabled:opacity-50"
					title={t("presetsPanel.sliderStrength")}
					defaultValue={1}
					value={presetStrength}
					min={0}
					max={200}
					step={1}
					onUpdate={(val) => {
						if (currentPreset) {
							previewPreset(currentPreset, val)
						}
					}}
					onUpdateEnded={(val) => {
						adjustPresetStrength(val)
					}}
					disabled={currentPreset == null}
				/>
			</div>
		)
	}

	const renderUserPresets = () => {
		return (
			<div>
				<ControlGroup
					expandKey={"user_presets_group"}
					key={"user_presets_group"}
					name={t("presetsPanel.userPresets")}
					disabled={isDisabled}
					compact={true}
					hideSeparator
				>
					{renderPresetsList(userPresetsGroup)}
				</ControlGroup>

				<div className="my-1 border-b border-base-900"></div>
			</div>
		)
	}

	const renderGroupPresets = () => {
		if (customGroups && Object.keys(customGroups).length > 0) {
			return (
				<div>
					{customGroups.map(([group, presets]) => (
						<ControlGroup
							expandKey={group}
							key={group}
							name={group}
							disabled={isDisabled}
							compact={true}
							className="group"
							hideSeparator
							accessoryView={
								<Button
									trackingLabel="open_preset_group_menu"
									variant="custom"
									size="custom"
									className="invisible -m-1 p-0.5 hover:text-base-400 group-hover:visible"
									onClick={(e) => openPresetGroupMenu(e, group)}
								>
									<Icon name="more" />
								</Button>
							}
							onContextMenu={(e) => {
								analytics.trackEvent(
									{
										label: "open_preset_group_menu",
										type: "click",
										clickType: "right_click",
										value: group,
									},
									e.target,
								)
								openPresetGroupMenu(e, group)
							}}
						>
							{renderPresetsList(presets)}
						</ControlGroup>
					))}

					<div className="my-1 border-b border-base-900"></div>
				</div>
			)
		}
	}

	const getPresetDisplayName = useCallback(
		(preset: PresetItem) => {
			if (preset.readOnly) {
				return getPackagedPresetDisplayName(preset.name, t)
			}
			return preset.name
		},
		[t],
	)

	const renderPolarrPresets = () => {
		return (
			<div>
				{Object.entries(polarrPresets).map(([group, presets]) => (
					<ControlGroup
						expandKey={group}
						key={group}
						name={
							packagedPresetCategoryKeys[group]
								? t(packagedPresetCategoryKeys[group])
								: group
						}
						disabled={isDisabled}
						compact={true}
						hideSeparator
					>
						{renderPresetsList(presets)}
					</ControlGroup>
				))}
			</div>
		)
	}
	return (
		<div className="flex w-full flex-col space-y-0">
			{renderPresetStrength()}

			<div
				className="flex h-fit w-full flex-col justify-start bg-base-800"
				aria-disabled={isDisabled}
			>
				{renderUserPresets()}
				{renderGroupPresets()}
				{renderPolarrPresets()}
			</div>
		</div>
	)
}

export const PresetsPanel = () => {
	const { t } = useTranslation("components")
	const editingDisabled = useAtomValue(editingDisabledAtom)
	const { data: userPresets } = useAtomValue(userPresetsAtom)
	const expandedPanels = useAtomValue(expandedPanelsAtom)
	const [pendingScrollPresetId, setPendingScrollPresetId] = useState<string | null>(null)

	const importErrorMap = new ErrorMap(
		[[isUnsupportedLRTemplateError, t("presetsPanel.importErrorUnsupportedTemplate")]],
		t("presetsPanel.importErrorDefault"),
	)

	const openPanelMenu = (e: React.MouseEvent) => {
		e.stopPropagation()
		const enableLRPresets = isLRPresetsEnabled()

		showContextMenu(
			e,
			<div aria-label={t("presetsPanel.ariaPresetPanelMenu")}>
				<ContextMenuItem
					trackingLabel="create_preset"
					aria-label={t("presetsPanel.ariaCreateNewPreset")}
					onSelect={() => replaceDialog(CreatePresetDialog)}
				>
					{t("presetsPanel.createPreset")}
				</ContextMenuItem>
				<ContextMenuSeparator />
				<ContextMenuItem
					trackingLabel="import_preset"
					aria-label={t("presetsPanel.ariaImportPreset")}
					onSelect={() =>
						void handleImportPreset({
							selectFolder: false,
							selectMultiple: true,
							allowedExtensions: DEFAULT_IMPORTABLE_PRESET_EXTENSIONS,
						})
					}
				>
					{t("presetsPanel.importCompanyPreset", { company: getCompanyNameShort() })}
				</ContextMenuItem>
				{enableLRPresets && (
					<ContextMenuItem
						trackingLabel="import_lr_presets"
						aria-label={t("presetsPanel.ariaImportLrPresets")}
						onSelect={() =>
							void handleImportPreset({
								selectFolder: false,
								selectMultiple: true,
								allowedExtensions: LIGHTROOM_IMPORTABLE_PRESET_EXTENSIONS,
							})
						}
					>
						{t("presetsPanel.importLRPresets")}
					</ContextMenuItem>
				)}
			</div>,
		)
	}

	const handleScrollToImportedPresetDone = useCallback(() => {
		setPendingScrollPresetId(null)
	}, [])

	const wait = (ms: number) =>
		new Promise<void>((resolve) => {
			setTimeout(resolve, ms)
		})

	const handleImportPreset = async (props: {
		selectFolder: boolean
		selectMultiple: boolean
		allowedExtensions?: PresetImportExtension[]
	}) => {
		try {
			const existingPresetIds = new Set(userPresets.map((preset) => preset.id))
			const { errors, presets, successCount } = await importPreset(props)
			if (errors && Object.keys(errors).length > 0) {
				showImportPresetErrorsDialog(errors)
			}

			if (successCount > 0 && presets.length > 0) {
				const newlyAddedPreset =
					[...presets].reverse().find((preset) => !existingPresetIds.has(preset.id)) ??
					presets.at(-1)
				if (newlyAddedPreset?.id) {
					const presetGroup = newlyAddedPreset.group ?? DEFAULT_PRESET_GROUP
					const groupExpandKey =
						presetGroup === DEFAULT_PRESET_GROUP ? "user_presets_group" : presetGroup
					const shouldExpandPanel = expandedPanels["presets_panel"] !== true
					const shouldExpandGroup = expandedPanels[groupExpandKey] !== true

					expandPanel("presets_panel", true)
					expandPanel(groupExpandKey, true)

					// Enforce ordering: expand first, then scroll.
					// 200ms matches ExpandablePanel animation duration; keep a small buffer.
					if (shouldExpandPanel || shouldExpandGroup) {
						await wait(PANEL_EXPAND_ANIMATION_MS)
					}
					setPendingScrollPresetId(newlyAddedPreset.id)
				}
			}
		} catch (e) {
			if (isAbortError(e)) {
				return
			}
			Sentry.captureException(e)
		}
	}

	const showImportPresetErrorsDialog = (errors: Record<string, Error>) => {
		replaceDialog(ConfirmDialog, {
			title: t("presetsPanel.errorImportingPresets"),
			embeddedComponent: (
				<div className="max-h-48 overflow-y-auto pb-2 text-start text-sm">
					<ul>
						{Object.entries(errors).map(([fileName, error]) => (
							<li key={fileName} className="text-sm text-base-400">
								{fileName}: {importErrorMap.getErrorMessage(error)}
							</li>
						))}
					</ul>
				</div>
			),
			primary: { title: t("common:action.confirm") },
			trackingViewName: "preset_import_error_dialog",
		})
	}

	return (
		<div className="flex w-full flex-col space-y-0">
			<ControlGroup
				className="sticky z-10 bg-base-800"
				disabled={editingDisabled}
				isSticky={true}
				expandKey="presets_panel"
				name={t("presetsPanel.panelPresets")}
				accessoryView={
					<Button
						trackingLabel="open_panel_menu"
						variant="custom"
						size="custom"
						onClick={openPanelMenu}
					>
						<Icon name="add" className="hover:text-base-400" />
					</Button>
				}
			>
				<Suspense fallback={<div className="size-full shrink-0 bg-base-800"></div>}>
					<PresetsView
						scrollToPresetId={pendingScrollPresetId}
						onScrollToPresetDone={handleScrollToImportedPresetDone}
					/>
				</Suspense>
			</ControlGroup>
		</div>
	)
}
